/**
 * Browser compatibility check utility
 */

// Minimum supported browser versions
const MIN_BROWSER_VERSIONS = {
  chrome: 70,
  firefox: 65,
  safari: 12,
  edge: 79,  // Chromium-based Edge
  opera: 60
};

// Check if browser is supported
export const isBrowserSupported = (): boolean => {
  const ua = navigator.userAgent;
  
  // Get browser name and version
  let browserName: string | null = null;
  let browserVersion = 0;
  
  // Chrome
  const chromeMatch = ua.match(/Chrome\/(\d+)/);
  if (chromeMatch && !ua.match(/Edg\/|OPR\//)) {
    browserName = 'chrome';
    browserVersion = parseInt(chromeMatch[1], 10);
  }
  
  // Firefox
  const firefoxMatch = ua.match(/Firefox\/(\d+)/);
  if (firefoxMatch) {
    browserName = 'firefox';
    browserVersion = parseInt(firefoxMatch[1], 10);
  }
  
  // Safari (not Chrome or Firefox)
  const safariMatch = ua.match(/Version\/(\d+).*Safari/);
  if (safariMatch && !chromeMatch && !firefoxMatch) {
    browserName = 'safari';
    browserVersion = parseInt(safariMatch[1], 10);
  }
  
  // Edge (Chromium-based)
  const edgeMatch = ua.match(/Edg\/(\d+)/);
  if (edgeMatch) {
    browserName = 'edge';
    browserVersion = parseInt(edgeMatch[1], 10);
  }
  
  // Opera
  const operaMatch = ua.match(/OPR\/(\d+)/);
  if (operaMatch) {
    browserName = 'opera';
    browserVersion = parseInt(operaMatch[1], 10);
  }
  
  // Compare with minimum supported versions
  if (browserName && MIN_BROWSER_VERSIONS[browserName]) {
    return browserVersion >= MIN_BROWSER_VERSIONS[browserName];
  }
  
  // If browser is not recognized, assume it's not supported
  return false;
};

// Check if device is supported (minimum screen size, etc.)
export const isDeviceSupported = (): boolean => {
  // Check for touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Check for minimum screen width
  const minScreenWidth = 320; // iPhone 5 width
  const hasMinScreenWidth = window.innerWidth >= minScreenWidth;
  
  return hasMinScreenWidth;
};

// Check for required features
export const requiredFeaturesSupported = (): boolean => {
  // Check for essential features
  const hasPromises = typeof Promise !== 'undefined';
  const hasFetch = typeof fetch !== 'undefined';
  const hasLocalStorage = typeof localStorage !== 'undefined';
  const hasFlexbox = CSS.supports('display', 'flex');
  
  return hasPromises && hasFetch && hasLocalStorage && hasFlexbox;
};